import { NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { HtmlPageMeta } from '~/components/HtmlPageMeta/HtmlPageMeta'
import { HtmlPageMetaResourceService } from '~/components/HtmlPageMeta/HtmlPageMetaResourceService'
import { UserHeader } from '~/components/UserGalleryPage/UserHeader'
import { Trophies } from '~/components/UserGalleryPage/Trophies'
import { UserProviderUserDtoTranslator } from '~/modules/Auth/Infrastructure/Translators/UserProviderUserDtoTranslator'
import { UserGalleryPageViewModel } from '~/modules/PresentationAdapters/UserGalleryPageAdapter'
import styles from './MarketUserGalleryPage.module.scss'
import { useEffect } from 'react'
import { getBrowserAnalyticsService } from '~/modules/Analytics/Infrastructure/safeAnalytics'

export interface MarketUserGalleryPageProps {
  viewModel: UserGalleryPageViewModel
}

export const MarketUserGalleryPage: NextPage<MarketUserGalleryPageProps> = ({ viewModel }) => {
  const { t } = useTranslation('user_gallery')
  const userDto = UserProviderUserDtoTranslator.publicFromApplication(viewModel.user)
  const htmlPageMetaUrlProps = (
    new HtmlPageMetaResourceService(
      t('user_gallery_page_title', { username: viewModel.user.name }),
      viewModel.profile.bio
        ? `${t('user_gallery_page_description', { username: viewModel.user.name })} ${viewModel.profile.bio}`
        : t('user_gallery_page_description', { username: viewModel.user.name })
    )
  ).getProperties()
  const htmlPageMetaProps = { ...viewModel.htmlPageMetaContextProps, ...htmlPageMetaUrlProps }

  const itemsCount = viewModel.products.length
  const itemsCountTranslation = itemsCount === 1 ? t('achievements_singular') : t('achievements_plural')

  useEffect(() => {
    async function onGalleryViewed () {
      const analytics = getBrowserAnalyticsService()

      if (analytics === null) {
        return
      }

      await analytics.trackGalleryViewed({
        url: htmlPageMetaProps.url,
        language: htmlPageMetaProps.locale,
      })
    }

    onGalleryViewed().catch(() => {
      console.error(`Failed to send Gallery Viewed event for URL ${htmlPageMetaProps.url}`)
    })
  }, [htmlPageMetaProps.url, htmlPageMetaProps.locale])

  return (
    <>
      <HtmlPageMeta { ...htmlPageMetaProps } />

      <section className={ styles.marketUserGalleryPage__container }>
        <UserHeader
          title={ htmlPageMetaProps.title }
          url={ htmlPageMetaProps.url }
          user={ userDto }
          profile={ viewModel.profile }
        />

        <section className={ styles.marketUserGalleryPage__overview }>
          <div className={ styles.marketUserGalleryPage__collectionHeader }>
            <div className={ styles.marketUserGalleryPage__collectionHeading }>
              <h2 className={ styles.marketUserGalleryPage__collectionTitle }>
                { t('overview_title', { defaultValue: t('achievements_title') }) }
              </h2>

              <p className={ styles.marketUserGalleryPage__count }>
                { itemsCount } { itemsCountTranslation }
              </p>
            </div>
          </div>

          <Trophies
            products={ viewModel.products }
            backlink={ true }
            appearance='gallery'
          />
        </section>
      </section>
    </>
  )
}
